import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, Activity, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { formatDate } from "@/lib/date";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceArea, ReferenceLine,
} from "recharts";

const GRANULARITIES = [
  ["weekly", "Weekly"],
  ["monthly", "Monthly"],
  ["quarterly", "Quarterly"],
];

const STATUS_COLORS = { NORMAL: "bg-green-100 text-green-700", HIGH: "bg-red-100 text-red-700", LOW: "bg-amber-100 text-amber-700" };
const STATUS_DOT = { NORMAL: "bg-green-500", HIGH: "bg-red-500", LOW: "bg-amber-500" };
const TREND_META = {
  INCREASING: { icon: TrendingUp, label: "Increasing" },
  DECREASING: { icon: TrendingDown, label: "Decreasing" },
  STABLE: { icon: Minus, label: "Stable" },
};
const slug = (s) => s.replace(/[^a-zA-Z0-9]/g, "-");
const VITALS_UNIT_SUFFIX = { "Weight": "kg", "Blood Pressure (Systolic)": "mmHg", "Blood Pressure (Diastolic)": "mmHg" };

export default function PatientVitalsTrendsTab({ patientId }) {
  const [trends, setTrends] = useState({});
  const [explanations, setExplanations] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedParam, setSelectedParam] = useState(null);
  const [granularity, setGranularity] = useState("monthly");
  const [explainLoading, setExplainLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: t }, { data: exp }] = await Promise.all([
        api.get(`/ai/patients/${patientId}/vitals-trends`),
        api.get(`/ai/patients/${patientId}/vitals-trends/explanations`),
      ]);
      setTrends(t);
      setExplanations(exp);
      const params = Object.keys(t);
      if (params.length > 0) setSelectedParam((prev) => (prev && t[prev] ? prev : params[0]));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const explainTrend = async () => {
    if (!selectedParam) return;
    setExplainLoading(true);
    try {
      const { data } = await api.post(`/ai/patients/${patientId}/vitals-trends/explain`, { parameter: selectedParam });
      setExplanations((prev) => ({ ...prev, [selectedParam]: data }));
      toast.success("Trend explained");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to explain trend");
    } finally {
      setExplainLoading(false);
    }
  };

  const params = Object.keys(trends);
  const current = selectedParam ? trends[selectedParam] : null;
  const explanation = selectedParam ? explanations[selectedParam] : null;
  const chartData = current?.points?.map((p) => ({ date: formatDate(p.date), value: p.value, in_range: p.in_range })) || [];
  const bucketData = current?.[granularity] || [];

  return (
    <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6" data-testid="patient-vitals-trends-tab">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-semibold text-slate-900">
          <Activity className="h-4 w-4 text-teal-700" /> AI Vitals &amp; Trends
        </h2>
        {!loading && params.length > 0 && (
          <Button data-testid="explain-trend-btn" size="sm" disabled={explainLoading || !selectedParam} onClick={explainTrend} className="gap-2 bg-teal-700 hover:bg-teal-800">
            <Sparkles className="h-3 w-3" /> {explainLoading ? "Explaining..." : "Explain Trend"}
          </Button>
        )}
      </div>

      {loading && <p className="mt-6 text-sm text-slate-400">Loading vitals...</p>}
      {!loading && params.length === 0 && (
        <p className="mt-6 text-sm text-slate-400" data-testid="vitals-empty-state">
          No vitals recorded yet — weight/BP entered during consultations and AI-extracted lab values will appear here.
        </p>
      )}

      {!loading && params.length > 0 && (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="vitals-param-selector">
            {params.map((p) => (
              <MetricCard key={p} name={p} data={trends[p]} selected={selectedParam === p} onClick={() => setSelectedParam(p)} />
            ))}
          </div>

          {current && (
            <div className="mt-5" data-testid="vitals-trend-detail">
              {explanation && (
                <div className="mb-4 rounded-lg border border-teal-200 bg-teal-50/50 p-4" data-testid="trend-explanation-result">
                  <p className="text-xs text-slate-500">Last generated {formatDate(explanation.generated_at)} by {explanation.generated_by}</p>
                  <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-slate-800">
                    {explanation.bullets.map((b, i) => <li key={`${i}-${b}`}>{b}</li>)}
                  </ul>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2" data-testid="vitals-analytics-row">
                {current.latest_status && (
                  <span data-testid="vitals-latest-status" className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_COLORS[current.latest_status]}`}>
                    Latest: {current.latest_status}
                  </span>
                )}
                {current.trend_direction && (
                  <span data-testid="vitals-trend-direction" className="flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                    {(() => {
                      const Icon = TREND_META[current.trend_direction].icon;
                      return <Icon className="h-3 w-3" />;
                    })()}
                    {TREND_META[current.trend_direction].label}
                    {current.percent_change != null && (
                      <span className="text-slate-400">({current.percent_change > 0 ? "+" : ""}{current.percent_change}%)</span>
                    )}
                  </span>
                )}
                {current.normal_range && (
                  <span className="text-xs text-slate-400" data-testid="vitals-normal-range">Normal Range: {current.normal_range}</span>
                )}
              </div>

              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <StatCard label="Latest" value={current.latest} unit={VITALS_UNIT_SUFFIX[selectedParam]} />
                <StatCard label="Average" value={current.average} unit={VITALS_UNIT_SUFFIX[selectedParam]} />
                <StatCard label="Min" value={current.min} unit={VITALS_UNIT_SUFFIX[selectedParam]} />
                <StatCard label="Max" value={current.max} unit={VITALS_UNIT_SUFFIX[selectedParam]} />
              </div>

              <div className="mt-4 h-64 rounded-lg border border-slate-200 p-2">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id={`main-grad-${slug(selectedParam)}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0f766e" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#0f766e" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" fontSize={11} />
                    <YAxis fontSize={11} domain={["auto", "auto"]} />
                    <Tooltip />
                    {(current.normal_low != null || current.normal_high != null) && (
                      <ReferenceArea
                        y1={current.normal_low ?? "auto"}
                        y2={current.normal_high ?? "auto"}
                        fill="#16a34a"
                        fillOpacity={0.08}
                        label={{ value: "Normal Range", position: "insideTopRight", fontSize: 10, fill: "#16a34a" }}
                      />
                    )}
                    <ReferenceLine y={current.average} stroke="#94a3b8" strokeDasharray="4 4" label={{ value: "Avg", fontSize: 10, fill: "#94a3b8", position: "right" }} />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="#0f766e"
                      strokeWidth={2.5}
                      fill={`url(#main-grad-${slug(selectedParam)})`}
                      dot={<TrendDot />}
                      activeDot={{ r: 6 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-1 flex gap-3 text-[11px] text-slate-400">
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-green-500" /> In normal range</span>
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-500" /> Out of range</span>
              </p>

              <div className="mt-4 flex gap-1 rounded-lg bg-slate-100 p-1 w-fit">
                {GRANULARITIES.map(([key, label]) => (
                  <button
                    key={key}
                    data-testid={`vitals-granularity-${key}`}
                    onClick={() => setGranularity(key)}
                    className={`rounded-md px-3 py-1 text-xs font-medium ${granularity === key ? "bg-white shadow text-slate-900" : "text-slate-500"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="mt-3 h-44 rounded-lg border border-slate-200 p-2">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={bucketData}>
                    <defs>
                      <linearGradient id={`bar-grad-${slug(selectedParam)}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0d9488" stopOpacity={1} />
                        <stop offset="100%" stopColor="#0d9488" stopOpacity={0.45} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="period" fontSize={11} />
                    <YAxis fontSize={11} domain={["auto", "auto"]} />
                    <Tooltip />
                    <ReferenceLine y={current.average} stroke="#94a3b8" strokeDasharray="4 4" />
                    <Bar dataKey="average" fill={`url(#bar-grad-${slug(selectedParam)})`} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TrendDot(props) {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null) return null;
  const color = payload.in_range === false ? "#dc2626" : payload.in_range === true ? "#16a34a" : "#0f766e";
  return <circle cx={cx} cy={cy} r={4} fill={color} stroke="#fff" strokeWidth={1.5} />;
}

function MetricCard({ name, data, selected, onClick }) {
  const sparkData = (data.points || []).map((p) => ({ value: p.value }));
  const Icon = data.trend_direction ? TREND_META[data.trend_direction].icon : Minus;
  const dotColor = data.latest_status ? STATUS_DOT[data.latest_status] : "bg-slate-300";
  const gradId = `spark-grad-${slug(name)}`;
  return (
    <button
      data-testid={`vitals-param-btn-${name}`}
      onClick={onClick}
      className={`flex flex-col items-start rounded-xl border p-3 text-left transition-all ${
        selected ? "border-teal-600 bg-teal-50 shadow-sm ring-1 ring-teal-600" : "border-slate-200 bg-white hover:border-teal-300"
      }`}
    >
      <div className="flex w-full items-center justify-between gap-1">
        <span className="truncate text-xs font-semibold text-slate-700" title={name}>{name}</span>
        <span className={`h-2 w-2 shrink-0 rounded-full ${dotColor}`} />
      </div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className="text-lg font-bold text-slate-900">{data.latest}</span>
        {VITALS_UNIT_SUFFIX[name] && <span className="text-xs font-normal text-slate-400">{VITALS_UNIT_SUFFIX[name]}</span>}
        <Icon className="h-3 w-3 text-slate-400" />
      </div>
      <div className="mt-1 h-8 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={sparkData}>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0d9488" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#0d9488" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area type="monotone" dataKey="value" stroke="#0d9488" strokeWidth={1.5} fill={`url(#${gradId})`} dot={false} isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </button>
  );
}

function StatCard({ label, value, unit }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3 text-center" data-testid={`vitals-stat-${label.toLowerCase()}`}>
      <p className="text-xs font-medium uppercase text-slate-500">{label}</p>
      <p className="mt-1 text-lg font-semibold text-slate-900">{value ?? "—"}{value != null && unit && <span className="ml-1 text-xs font-normal text-slate-400">{unit}</span>}</p>
    </div>
  );
}
